import clsx from "clsx";

// Hack Club Auth exposes no picture claim (its userinfo has no picture/image
// field at all), and the old avatar path went through Slack, which needs a
// slack_id this OAuth app isn't granted. So most people have no photo, and the
// fallback is the common case rather than the exception -- worth making it look
// deliberate instead of showing everyone the same grey "?".
// No goldenrod or sidecar here on purpose: both are pale yellows, and the user
// card they most often sit on is goldenrod, so those tints disappear.
const TINTS = [
  "bg-viking",
  "bg-azalea",
  "bg-leaf",
  "bg-carrot-orange",
  "bg-sunset-orange",
] as const;

const SIZES = {
  sm: "h-8 w-8 text-caption",
  md: "h-10 w-10 text-body",
  lg: "h-14 w-14 text-subheading",
} as const;

// Deterministic so someone keeps the same colour everywhere they appear.
function tintFor(seed: string | number) {
  const str = String(seed);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  }
  return TINTS[Math.abs(hash) % TINTS.length];
}

function initials(name?: string | null) {
  const cleaned = (name ?? "").trim();
  if (!cleaned) return "?";

  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();

  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

export default function Avatar({
  src,
  name,
  seed,
  size = "md",
  className,
}: {
  src?: string | null;
  name?: string | null;
  seed?: string | number;
  size?: keyof typeof SIZES;
  className?: string;
}) {
  const classes = clsx(
    "border-line shrink-0 overflow-hidden rounded-full border-2",
    SIZES[size],
    className,
  );

  if (src) {
    return <img src={src} alt="" className={clsx(classes, "object-cover")} />;
  }

  return (
    <div
      aria-hidden
      className={clsx(
        classes,
        tintFor(seed ?? name ?? "?"),
        "font-display text-ink flex items-center justify-center font-extrabold",
      )}
    >
      {initials(name)}
    </div>
  );
}
